import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Card, CardContent, TextField, Typography, Alert,
  CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { authApi } from '../api/auth';
import { useAuthStore } from '../stores/authStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { data } = await authApi.login({ email, password, mfaCode: mfaCode || undefined });

      if (data.mfaRequired) {
        setMfaRequired(true);
        setLoading(false);
        return;
      }

      // Decode a minimal user from the token (or use a /me endpoint)
      const payload = JSON.parse(atob(data.accessToken.split('.')[1]));
      login(
        { id: payload.sub, email: payload.email ?? email, firstName: payload.firstName ?? 'User',
          lastName: payload.lastName ?? '', mfaEnabled: false },
        data.accessToken,
        data.refreshToken,
      );
      navigate('/dashboard');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      setError(msg || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: 'grey.100' }}>
      <Card sx={{ maxWidth: 420, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <LockOutlinedIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="h5">Sign In</Typography>
            <Typography variant="body2" color="text.secondary">Financial System</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField label="Email" type="email" fullWidth required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} />

            <TextField label="Password" type={showPassword ? 'text' : 'password'} fullWidth required
              value={password} onChange={(e) => setPassword(e.target.value)} sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end"
                      aria-label={showPassword ? 'hide password' : 'show password'}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }} />

            {mfaRequired && (
              <TextField label="MFA Code" fullWidth required autoFocus
                value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} sx={{ mb: 2 }}
                placeholder="Enter 6-digit code" inputProps={{ maxLength: 6 }} />
            )}

            <Button type="submit" variant="contained" fullWidth size="large" disabled={loading}
              sx={{ mt: 1 }}>
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
